import { NurseLayout } from "@/components/layout/nurse-layout";
import { useListScenarios } from "@workspace/api-client-react";
import { useScrollRestore } from "@/hooks/use-scroll-restore";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileQuestion, PhoneCall, AlertTriangle, Award, Flame, ChevronRight } from "lucide-react";
import { useMemo, useState, useEffect } from "react";

const TYPE_TABS = [
  { value: "all", label: "All" },
  { value: "advice_line", label: "Advice Line" },
  { value: "nmc_mock", label: "NMC Mock" },
  { value: "ftp_daily_drip", label: "FTP Daily Drip" },
  { value: "rcn_competency", label: "RCN Competency" },
] as const;

const DIFFICULTY_FILTERS = [
  { value: "", label: "All levels" },
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
];

function TypeBadge({ type }: { type: string }) {
  if (type === "nmc_mock") {
    return (
      <Badge className="bg-slate-800 text-white hover:bg-slate-800">
        <span className="flex items-center gap-1">
          <FileQuestion className="h-3 w-3" /> NMC Mock
        </span>
      </Badge>
    );
  }
  if (type === "ftp_daily_drip") {
    return (
      <Badge className="bg-red-700 text-white hover:bg-red-700">
        <span className="flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> FTP Daily Drip
        </span>
      </Badge>
    );
  }
  if (type === "rcn_competency") {
    return (
      <Badge className="bg-teal-700 text-white hover:bg-teal-700">
        <span className="flex items-center gap-1">
          <Award className="h-3 w-3" /> RCN Competency
        </span>
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">
      <span className="flex items-center gap-1">
        <PhoneCall className="h-3 w-3" /> Advice Line
      </span>
    </Badge>
  );
}

function difficultyClass(d: string) {
  if (d === "advanced") return "bg-rose-100 text-rose-700";
  if (d === "intermediate") return "bg-amber-100 text-amber-700";
  return "bg-green-100 text-green-700";
}

function getDailyDripIndex(total: number): number {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now.getTime() - start.getTime();
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
  return dayOfYear % total;
}

function DailyDripBanner({
  scenario,
}: {
  scenario: { id: number; title: string; description: string; difficulty: string; category?: string | null };
}) {
  const today = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="mb-6 rounded-2xl overflow-hidden border border-red-200 shadow-sm">
      <div className="bg-gradient-to-r from-red-900 to-red-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 rounded-xl p-2.5">
            <Flame className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-xs font-semibold text-red-200 uppercase tracking-wider">FTP Daily Drip</p>
            <p className="text-white font-bold text-base leading-tight">Today's Fitness to Practise Case</p>
          </div>
        </div>
        <p className="text-red-200 text-xs hidden sm:block">{today}</p>
      </div>

      <div className="bg-white px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-xs">
              {scenario.difficulty}
            </Badge>
            {scenario.category && (
              <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">
                {scenario.category}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-slate-900 text-base leading-snug line-clamp-2">
            {scenario.title}
          </h3>
          <p className="text-sm text-slate-500 mt-1.5 line-clamp-2">{scenario.description}</p>
        </div>
        <Link href={`/nurse/scenarios/${scenario.id}`} className="flex-shrink-0">
          <Button className="bg-red-700 hover:bg-red-800 text-white gap-2 w-full sm:w-auto">
            Start case <ChevronRight className="h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function NurseScenarios() {
  useScrollRestore("nurse-scenarios");
  const [activeType, setActiveType] = useState<string>(
    () => localStorage.getItem("rheumhub:pref:nurse-scenarios:type") ?? "all"
  );
  const [activeDifficulty, setActiveDifficulty] = useState<string>(
    () => localStorage.getItem("rheumhub:pref:nurse-scenarios:difficulty") ?? ""
  );

  useEffect(() => {
    if (activeType !== "all") localStorage.setItem("rheumhub:pref:nurse-scenarios:type", activeType);
    else localStorage.removeItem("rheumhub:pref:nurse-scenarios:type");
  }, [activeType]);

  useEffect(() => {
    if (activeDifficulty) localStorage.setItem("rheumhub:pref:nurse-scenarios:difficulty", activeDifficulty);
    else localStorage.removeItem("rheumhub:pref:nurse-scenarios:difficulty");
  }, [activeDifficulty]);

  const { data: scenarios, isLoading } = useListScenarios({});

  const ftpScenarios = useMemo(
    () => (scenarios ?? []).filter(s => s.type === "ftp_daily_drip"),
    [scenarios],
  );

  const dailyDrip = useMemo(() => {
    if (ftpScenarios.length === 0) return null;
    const idx = getDailyDripIndex(ftpScenarios.length);
    return ftpScenarios[idx];
  }, [ftpScenarios]);

  const filtered = useMemo(() => {
    if (!scenarios) return [];
    return scenarios.filter(s => {
      const matchType = activeType === "all" || s.type === activeType;
      const matchDiff = !activeDifficulty || s.difficulty === activeDifficulty;
      return matchType && matchDiff;
    });
  }, [scenarios, activeType, activeDifficulty]);

  const counts = useMemo(() => {
    if (!scenarios) return {} as Record<string, number>;
    const c: Record<string, number> = { all: scenarios.length };
    for (const s of scenarios) {
      c[s.type] = (c[s.type] ?? 0) + 1;
    }
    return c;
  }, [scenarios]);

  if (isLoading) return <NurseLayout title="Clinical Scenarios"><LoadingScreen /></NurseLayout>;

  return (
    <NurseLayout title="Clinical Scenarios">
      {dailyDrip && <DailyDripBanner scenario={dailyDrip} />}

      <div className="mb-6">
        <p className="text-slate-600 mb-5">
          Practice your clinical reasoning with advice line situations, NMC Code mock scenarios, FTP fitness-to-practise cases, and RCN competency-mapped reflections.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {TYPE_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveType(tab.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all flex items-center gap-1.5 ${
                activeType === tab.value
                  ? "bg-teal-700 text-white border-teal-700 shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:border-teal-400"
              }`}
            >
              {tab.label}
              <span className={`text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center leading-none ${
                activeType === tab.value ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-500"
              }`}>
                {counts[tab.value] ?? 0}
              </span>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {DIFFICULTY_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setActiveDifficulty(f.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                activeDifficulty === f.value
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && (
        <div className="py-16 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">
          No scenarios found for the selected filters.
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {filtered.map(s => (
          <Link key={s.id} href={`/nurse/scenarios/${s.id}`}>
            <Card className="h-full hover:border-teal-500 hover:shadow-md transition-all cursor-pointer">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start mb-2">
                  <TypeBadge type={s.type} />
                  <Badge variant="secondary" className={difficultyClass(s.difficulty)}>
                    {s.difficulty}
                  </Badge>
                </div>
                <CardTitle className="text-lg group-hover:text-teal-700">{s.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 line-clamp-3">{s.description}</p>
                {s.category && (
                  <p className="text-xs text-slate-400 mt-4 font-medium uppercase tracking-wider">
                    {s.category}
                  </p>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </NurseLayout>
  );
}
